import RightSideDetailsPanel, { COLUMN } from "Areas/SharedFeatures/rightSideDetailsPanel";
import useBeforeUnloadMessage from "Common/componentUtils/useBeforeUnloadMessage";
import useHistory from "Common/componentUtils/useHistory";
import useKeyboardEventListener from "Common/componentUtils/useKeyboardEventListener";
import ProgressOverlay from "Components/progressOverlay";
import Scrollbars from "Components/scrollbars";
import WelcomePanel from "Components/welcomePanel";
import classNames from "classnames";
import filter from "lodash/filter";
import find from "lodash/find";
import head from "lodash/head";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import values from "lodash/values";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useResizeDetector } from "react-resize-detector";
import { READ_ONLY_MODE } from "../documentDetails/types/readOnlyMode";
import { sendToGraphButtonAttrs, sendToGraphSelector } from "./constants";
import locale from "./content.locale";
import RowActionsPanel from "./rowActionsPanel";
import SwitcherFeed from "./switcherFeed";
const detailsPanelTableColumns = [COLUMN.OBJECT_TYPE, COLUMN.ID, COLUMN.COLOR];
function Content(props) {
    const { undo, redo, clearHistory, addToHistory, deleted, deletedRef } = useHistory();
    const { height, ref: observableRef } = useResizeDetector();
    const hasChangesRef = useRef(false);
    const [hasChanges, setHasChanges] = useState(false);
    const dataRef = useRef();
    const sourceDataRef = useRef(props.sourceData);
    const readOnlyModeRef = useRef(props.readOnlyMode);
    useEffect(() => {
        props.onInitialized && props.onInitialized();
    }, []);
    const selected = useMemo(() => (props.selected ? filter(props.selected, s => !includes(deleted, s)) : undefined), [props.selected, deleted]);
    const selectedMasters = useMemo(() => filter(props.masterNodes, node => includes(selected, node.NodeId)), [props.masterNodes, selected]);
    const headMaster = useMemo(() => head(selectedMasters), [selectedMasters]);
    const selectedObject = useMemo(() => props.documentObjects && find(props.documentObjects, object => includes(selected, object.Id)), [props.documentObjects, selected]);
    const selectedChildren = useMemo(() => {
        let children = filter(props.childrenNodes &&
            headMaster &&
            selectedObject &&
            props.childrenNodes[headMaster.NodeId] &&
            props.childrenNodes[headMaster.NodeId][selectedObject.Id], node => includes(selected, node.NodeId));
        if (!children.length &&
            props.childrenNodes &&
            headMaster &&
            selectedObject &&
            props.childrenNodes[headMaster.NodeId]) {
            children = values(props.childrenNodes[headMaster.NodeId][selectedObject.Id]);
        }
        return children;
    }, [props.childrenNodes, headMaster, selectedObject, selected]);
    const selectedNodeSources = useMemo(() => values(selectedChildren), [selectedChildren]);
    useEffect(() => {
        hasChangesRef.current = hasChanges;
    }, [hasChanges]);
    useEffect(() => {
        readOnlyModeRef.current = props.readOnlyMode;
    }, [props.readOnlyMode]);
    useBeforeUnloadMessage(hasChangesRef);
    useEffect(() => {
        sourceDataRef.current = props.sourceData;
        clearHistory();
        setHasChanges(false);
    }, [props.sourceData]);
    useEffect(() => {
        dataRef.current = props.data;
    }, [props.data]);
    useEffect(() => {
        props.onChangeDeleted && props.onChangeDeleted(deleted);
    }, [deleted]);
    useEffect(() => {
        !props.masterNodes &&
            props.documentId &&
            props.onGetContentData &&
            props.onGetContentData(props.documentId);
    }, [props.documentId, props.onGetContentData, props.masterNodes]);
    const savingCallback = useCallback((documentId) => () => {
        if (hasChangesRef.current &&
            sourceDataRef.current &&
            dataRef.current &&
            props.canEdit &&
            readOnlyModeRef.current === READ_ONLY_MODE.READ_WRITE &&
            props.onSaveContentData) {
            props.onSaveContentData(documentId, sourceDataRef.current, filter(dataRef.current, d => !includes(deletedRef.current, d.Id)));
        }
    }, [props.onSaveContentData]);
    useEffect(() => () => {
        props.documentId && savingCallback(props.documentId)();
        clearHistory();
    }, [props.documentId]);
    const handleUndo = useCallback(() => {
        if (undo) {
            const undoData = undo();
            props.documentId &&
                undoData.modifiedData &&
                props.onChangeContentData &&
                props.onChangeContentData(props.documentId, undoData.modifiedData);
            setHasChanges(undoData.canUndo);
        }
    }, [props.documentId, props.onChangeContentData, undo]);
    const handleRedo = useCallback(() => {
        if (redo) {
            const redoData = redo();
            props.documentId &&
                redoData.modifiedData &&
                props.onChangeContentData &&
                props.onChangeContentData(props.documentId, redoData.modifiedData);
            setHasChanges(true);
        }
    }, [props.documentId, props.onChangeContentData, redo]);
    const handleRefresh = useCallback(() => {
        props.documentId && props.onGetContentData && props.onGetContentData(props.documentId);
    }, [props.documentId, props.onGetContentData]);
    const handleDelete = useCallback(() => {
        if (props.documentId && selected && props.canEdit) {
            addToHistory("delete", selected);
            setHasChanges(true);
        }
    }, [props.documentId, selected, props.canEdit]);
    useKeyboardEventListener("Delete", false, handleDelete);
    const handleDataUpdate = useCallback((ids, fieldName, value) => {
        if (!props.documentId || !props.onChangeContentData)
            return [];
        const toUpdate = filter(dataRef.current, d => ids.has(d.Id));
        if (isEmpty(toUpdate))
            return [];
        const updated = map(toUpdate, e => ({
            ...e,
            [fieldName]: value
        }));
        addToHistory("update", map(updated, "Id"), toUpdate, updated);
        props.onChangeContentData(props.documentId, updated);
        setHasChanges(true);
        return updated;
    }, [props.documentId, props.onChangeContentData]);
    const containerStyle = useMemo(() => ({ paddingBottom: props.bottomOffset }), [props.bottomOffset]);
    const handleGetPreview = useCallback((_, __, ___, largeAttributeId, completed, rejected) => {
        if (largeAttributeId) {
            props.onGetPreview(largeAttributeId, completed, rejected);
        }
        else {
            rejected && rejected();
        }
    }, [props.onGetPreview]);
    useEffect(() => {
        props.onInProgressChanged && props.onInProgressChanged(Boolean(props.inProgress));
    }, [props.inProgress]);
    if (!props.documentId)
        return null;
    return (React.createElement(ProgressOverlay, { show: props.inProgress, className: classNames("overflow-hidden", props.className) },
        React.createElement("div", { className: "grid-rows-auto-1fr", style: containerStyle },
            React.createElement(RowActionsPanel, { rightSidePanel: React.createElement(RightSideDetailsPanel, { openedHeight: height ? height + (props.bottomOffset || 0) : undefined, closedHeight: props.rowActionsPanelHeight, documentId: props.documentId, open: props.detailedPanelOpen, autoOpenByDataLength: false, onChangeOpen: props.onChangeDetailedPanelOpen, data: selectedNodeSources, onDataUpdate: props.canEdit ? handleDataUpdate : undefined, canModifyAttributes: false, documentObjects: props.documentObjects, tableColumns: detailsPanelTableColumns, onGetPreview: handleGetPreview }), rowActionsPanelHeight: props.rowActionsPanelHeight, onSave: props.documentId &&
                    hasChanges &&
                    props.canEdit &&
                    props.readOnlyMode === READ_ONLY_MODE.READ_WRITE
                    ? savingCallback(props.documentId)
                    : undefined, onDelete: (selected === null || selected === void 0 ? void 0 : selected.length) && props.canEdit ? handleDelete : undefined, onUndo: undo && props.canEdit ? handleUndo : undefined, onRedo: redo && props.canEdit ? handleRedo : undefined, onRefresh: handleRefresh, sendToGraphButtonAttrs: sendToGraphButtonAttrs, sendToGraphSelector: sendToGraphSelector }),
            React.createElement("div", { className: "position-relative overflow-hidden", ref: observableRef },
                React.createElement("div", { className: "p-xs stretched-to-fit h-100-max overflow-hidden" },
                    !props.masterNodes && !props.inProgress && (React.createElement(WelcomePanel, { messageClassName: "font-base", showIcon: false, message: locale.NO_DATA_TEXT })),
                    props.masterNodes && selectedNodeSources.length === 0 && (React.createElement(WelcomePanel, { messageClassName: "font-base", showIcon: false, message: locale.SELECT_NAVIGATION_OBJECT })),
                    selectedNodeSources && (React.createElement(Scrollbars, { key: map(selectedNodeSources, "NodeId").join(",") },
                        React.createElement("div", { className: "flex-box-centered-content d-flex-column h-100" },
                            React.createElement(SwitcherFeed, { key: "SwitcherFeed", documentId: props.documentId, nodeSources: selectedNodeSources, documentObjects: props.documentObjects, masterNodeSource: headMaster, sendToGraphSelector: sendToGraphSelector })))))))));
}
export default memo(Content);
